import { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, Download, Link2 } from 'lucide-react';
import { ThemeToggle } from '@tark/shared';
import BrandMark from '../components/BrandMark';
import releaseNotes from '../features/releases/release-notes.json';
import InstallGuide from '../features/releases/InstallGuide';

const releases = releaseNotes.filter((release) => release.published);
const dateFormat = new Intl.DateTimeFormat('en-US', {
  month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
});

export default function ReleasesPage() {
  const [searchParams] = useSearchParams();
  const installing = searchParams.get('view') === 'install';
  const platform = searchParams.get('platform') === 'ios' ? 'ios' : 'android';
  useEffect(() => {
    const previousTitle = document.title;
    document.title = "What's New | TARK";
    return () => { document.title = previousTitle; };
  }, []);

  return (
    <div className="min-h-screen bg-background text-text tracking-normal [overflow-wrap:anywhere]">
      <header className="border-b border-border">
        <div className="mx-auto flex max-w-2xl flex-wrap items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <Link to="/" aria-label="TARK home" className="rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary">
            <BrandMark size="sm" titleClassName="tracking-normal" className="[&>img]:h-[44px] [&>img]:w-[44px]" />
          </Link>
          <div className="[&>button]:min-h-[44px] [&>button]:min-w-[44px] [&>button]:text-text">
            <ThemeToggle />
          </div>
        </div>
      </header>

      <main id="main-content" className="mx-auto max-w-2xl px-4 pb-12 pt-8 sm:px-6">
        <h1 className="text-3xl font-bold leading-tight">What's New</h1>
        <p className="mt-2 text-sm text-text-muted">TARK release notes</p>

        <nav aria-label="Release page" className="mt-5 flex border-b border-border">
          {[{ label: 'Release notes', to: '/releases', selected: !installing }, { label: 'Install / Update', to: '/releases?view=install', selected: installing }].map((item) => (
            <Link key={item.label} to={item.to} aria-current={item.selected ? 'page' : undefined}
              className={`flex min-h-[48px] flex-1 items-center justify-center border-b-2 px-2 py-3 text-center text-sm font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary ${item.selected ? 'border-primary text-primary' : 'border-transparent text-text-muted hover:text-text'}`}>
              {item.label}
            </Link>
          ))}
        </nav>

        {installing ? <InstallGuide platform={platform} /> : <>
        <nav aria-label="Get TARK" className="mt-5 flex flex-wrap gap-3">
          <a href="https://testflight.apple.com/join/fMMeJQjU"
            className="inline-flex min-h-[44px] max-w-full items-center gap-2 rounded-lg border border-border bg-surface px-4 py-3 text-sm font-semibold text-text hover:border-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary">
            <Download aria-hidden="true" className="h-5 w-5 shrink-0" />
            <span className="min-w-0">iOS · TestFlight</span>
          </a>
          <a href="https://play.google.com/store/apps/details?id=com.playtark.app"
            className="inline-flex min-h-[44px] max-w-full items-center gap-2 rounded-lg border border-border bg-surface px-4 py-3 text-sm font-semibold text-text hover:border-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary">
            <Download aria-hidden="true" className="h-5 w-5 shrink-0" />
            <span className="min-w-0">Android · Google Play</span>
          </a>
        </nav>
        <p className="mt-2 text-xs leading-relaxed text-text-muted">Beta access and available builds depend on your tester group.</p>

        <div className="mt-6 divide-y divide-border">
          {releases.map((release) => (
            <article key={release.id} id={release.id} aria-labelledby={`${release.id}-title`} className="scroll-mt-6 py-6 first:pt-0">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-text-muted">
                    <time dateTime={release.date}>{dateFormat.format(new Date(`${release.date}T00:00:00Z`))}</time>
                    <span className="font-medium text-primary">{release.audience}</span>
                  </div>
                  <h2 id={`${release.id}-title`} className="mt-2 text-xl font-semibold leading-snug">{release.title}</h2>
                  <p className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-text-muted">
                    {release.platforms.map((platform) => <span key={platform}>{platform}</span>)}
                  </p>
                </div>
                <a href={`#${release.id}`} aria-label={`Link to ${release.title}`} title={`Link to ${release.title}`}
                  className="inline-flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-lg text-text-muted hover:bg-surface hover:text-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary">
                  <Link2 aria-hidden="true" className="h-4 w-4" />
                </a>
              </div>

              <ul className="mt-4 list-disc space-y-2 pl-5 text-base leading-relaxed marker:text-primary">
                {release.bullets.map((bullet) => <li key={bullet} className="pl-1 [overflow-wrap:anywhere]">{bullet}</li>)}
              </ul>
              {release.notice ? (
                <p className="mt-4 border-l-2 border-primary pl-3 text-sm leading-relaxed text-text-muted">{release.notice}</p>
              ) : null}
            </article>
          ))}
        </div>
        </>}

        <footer className="border-t border-border pt-4">
          <Link to="/" className="inline-flex min-h-11 items-center gap-2 rounded-lg text-sm font-semibold text-primary hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary">
            Open TARK <ArrowRight aria-hidden="true" className="h-4 w-4" />
          </Link>
        </footer>
      </main>
    </div>
  );
}
